import { useState } from "react";
import { Link } from "react-router-dom";
import SelectableCard, { useCardColors } from "../components/SelectableCard";
import EmailMeButton from "../components/EmailMeButton";
import { Check, ChevronDown } from "lucide-react";
import { useContent } from "../content/ContentContext";
import { SectionLabel, ButtonLink } from "../components/Ui";
import { Reveal } from "../components/Reveal";

function ServiceCard({ s, number, delay, selected, onSelect }) {
  const c = useCardColors();
  const [open, setOpen] = useState(false);
  const key = (s.includes || []).slice(0, 3);
  const more = (s.includes || []).slice(3);
  const hasDetails = more.length > 0 || s.minimumNote || s.scopeNote;
  return (
    <Reveal delay={delay} className="h-full">
      <SelectableCard
        id={s.id}
        testid={`service-card-${s.id}`}
        selected={selected}
        onSelect={onSelect}
        className="flex h-full scroll-mt-28 flex-col p-8 lg:p-9"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">
          S/{String(number).padStart(2, "0")}
        </p>
        <h3 className="mt-3 font-serif text-xl font-medium leading-snug" style={{ color: c.heading }}>{s.name}</h3>
        <p className="mt-3 font-sans text-2xl font-extrabold tracking-tight" style={{ color: c.heading }} data-testid={`service-price-${s.id}`}>
          {s.priceText}
        </p>
        <p className="mt-4 text-sm leading-relaxed" style={{ color: c.body }}>{s.summary}</p>
        <ul className="mt-5 space-y-2.5">
          {key.map((inc) => (
            <li key={inc} className="flex gap-2.5 text-sm leading-snug" style={{ color: c.body }}>
              <Check aria-hidden="true" strokeWidth={2.5} className="mt-0.5 h-4 w-4 shrink-0" style={{ color: c.check }} />
              {inc}
            </li>
          ))}
        </ul>
        {hasDetails && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-controls={`service-details-${s.id}`}
              data-testid={`service-details-toggle-${s.id}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-plum underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {open ? "Hide details" : "What's included"}
              <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div id={`service-details-${s.id}`} data-testid={`service-details-${s.id}`} className="mt-3 space-y-3">
                {more.length > 0 && (
                  <ul className="space-y-2.5">
                    {more.map((inc) => (
                      <li key={inc} className="flex gap-2.5 text-sm leading-snug" style={{ color: c.body }}>
                        <Check aria-hidden="true" strokeWidth={2.5} className="mt-0.5 h-4 w-4 shrink-0" style={{ color: c.check }} />
                        {inc}
                      </li>
                    ))}
                  </ul>
                )}
                {s.minimumNote && (
                  <p className="text-xs leading-relaxed" style={{ color: c.body }}>
                    <span className="font-semibold">Minimum: </span>{s.minimumNote}
                  </p>
                )}
                {s.scopeNote && (
                  <p className="border-t border-line pt-3 text-xs leading-relaxed text-muted" data-testid={`service-scope-${s.id}`}>
                    <span className="font-semibold" style={{ color: c.body }}>Scope note: </span>{s.scopeNote}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex-1" />
        <ButtonLink
          to={`/?service=${encodeURIComponent(s.name)}#contact`}
          testid={`service-discuss-${s.id}`}
          className="mt-7"
        >
          {s.cta} <span aria-hidden="true">→</span>
        </ButtonLink>
      </SelectableCard>
    </Reveal>
  );
}

export default function ServicesSection() {
  const { content } = useContent();
  const { site, services } = content;
  const [selectedService, setSelectedService] = useState(null);
  const automation = services.find((x) => x.id === "automation");
  const visible = services.filter((s) => !s.hidden);
  const groups = site.serviceGroups
    .map((g) => ({ ...g, items: visible.filter((s) => s.group === g.id) }))
    .filter((g) => g.items.length > 0);
  let counter = 0;

  return (
    <section id="services" data-testid="section-services" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
      <Reveal>
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionLabel>Services</SectionLabel>
            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="services-page-heading">
              {site.servicesHeading}
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted">{site.servicesIntro}</p>
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <figure className="overflow-hidden rounded-2xl border border-line bg-white" data-testid="services-intro-image">
              <img src={site.servicesImage.src} alt={site.servicesImage.alt} className="h-64 w-full object-cover" />
            </figure>
          </div>
        </div>
      </Reveal>

      {groups.map((g, gi) => (
        <div key={g.id} id={`group-${g.id}`} className="mt-12 scroll-mt-24" data-testid={`service-group-${g.id}`}>
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-accent">0{gi + 1}</p>
            <h2 className="mt-3 font-serif text-2xl font-medium tracking-tight text-ink sm:text-3xl">{g.title}</h2>
          </Reveal>
          <div className={`mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8 ${g.items.length >= 3 ? "xl:grid-cols-3" : ""}`}>
            {g.items.map((s, i) => {
              counter += 1;
              return <ServiceCard key={s.id} s={s} number={counter} delay={(i % 3) * 0.08} selected={selectedService === s.id} onSelect={() => setSelectedService(s.id)} />;
            })}
          </div>
        </div>
      ))}

      <Reveal className="mt-12">
        <div data-testid="home-hourly-band" className="rounded-2xl border border-line bg-lavender/50 p-8 sm:p-10">
          <h3 className="font-serif text-xl font-medium text-ink sm:text-2xl">{site.hourlyBand.heading}</h3>
          <p className="mt-3 max-w-3xl leading-relaxed text-muted">&ldquo;{site.hourlyBand.body}&rdquo;</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <ButtonLink to="/#package-hourly-support" testid="home-hourly-link">Explore hourly support <span aria-hidden="true">→</span></ButtonLink>
            <ButtonLink to={`/?service=${encodeURIComponent(automation ? automation.name : "Reporting & Workflow Automation")}#contact`} variant="secondary" testid="home-automation-link">
              Discuss automation <span aria-hidden="true">→</span>
            </ButtonLink>
            <Link to="/#pricing" data-testid="services-pricing-link" className="inline-flex items-center rounded-full border border-line bg-white px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-plum hover:text-plum">
              Compare packages &amp; pricing <span aria-hidden="true" className="ml-2">→</span>
            </Link>
            <EmailMeButton testid="home-services-email-button" className="rounded-full py-3" />
          </div>
        </div>
      </Reveal>
    </section>
  );
}
